/**
 * O painel do eixo "Contrato e apoio" (E98.F7.T01, ADR-0083 §2.5–§2.6).
 *
 * Mostra só o que o motor gravou na CCB: o bem financiado como cada documento o descreve, os
 * campos essenciais que outro signatário preencheu depois do cliente — com quem, quando, página
 * e se é fato apurado ou indício —, as conferências do kit e dos documentos de apoio e as
 * diligências. Nenhuma alegação nasce aqui: a tela lê, não conclui.
 */
import {
  Badge,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { chaveDoRotulo } from '@/lib/chave_do_rotulo';
import type { ItemVerificacaoData } from '@/lib/exame_eletronico';
import {
  contratoDoResultado,
  itensDoContrato,
  type ApoioApuradoData,
} from '@/lib/exame_eletronico_contrato';
import type { BemLidoData, CampoPreenchidoDepoisData } from '@/lib/exame_eletronico_kit';

import { ComoLer, type PainelDoEixoProps } from './QuadroPorEixosPaineis';
import { SituacaoBadge, Titulo, formatarInstante } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.contrato;
const te = ptBR.exameEletronico.eixos;
const REAIS = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function valorOuTraco(valor: string | number | null | undefined): string {
  return valor === null || valor === undefined || valor === '' ? t.semValor : String(valor);
}

function anoDoBem(bem: BemLidoData): string {
  if (bem.ano_modelo == null) return t.semValor;
  return bem.ano_fabricacao == null
    ? String(bem.ano_modelo)
    : `${bem.ano_fabricacao}/${bem.ano_modelo}`;
}

function BemFinanciado({ bens }: { bens: readonly BemLidoData[] }): JSX.Element | null {
  if (bens.length === 0) return null;
  return (
    <section data-testid="quadro-contrato-bens">
      <Titulo>{t.bemTitulo}</Titulo>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.colunaDocumento}</TableHead>
              <TableHead>{t.colunaVeiculo}</TableHead>
              <TableHead>{t.colunaAno}</TableHead>
              <TableHead>{t.colunaCor}</TableHead>
              <TableHead>{t.colunaChassi}</TableHead>
              <TableHead>{t.colunaValor}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bens.map((bem) => (
              <TableRow key={bem.documento} data-testid={`quadro-contrato-bem-${bem.documento}`}>
                <TableCell className="whitespace-nowrap">{bem.documento}</TableCell>
                <TableCell>{valorOuTraco(bem.marca_modelo)}</TableCell>
                <TableCell>{anoDoBem(bem)}</TableCell>
                <TableCell>{valorOuTraco(bem.cor)}</TableCell>
                <TableCell>{bem.chassi ? <MonoData>{bem.chassi}</MonoData> : t.semValor}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {bem.valor == null ? t.semValor : REAIS.format(Number(bem.valor))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function PreenchidosDepois({
  campos,
}: {
  campos: readonly CampoPreenchidoDepoisData[];
}): JSX.Element {
  return (
    <section data-testid="quadro-contrato-preenchidos">
      <Titulo>{t.preenchidosTitulo}</Titulo>
      {campos.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.preenchidosVazio}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.colunaCampo}</TableHead>
                  <TableHead>{t.colunaValorPreenchido}</TableHead>
                  <TableHead>{t.colunaQuem}</TableHead>
                  <TableHead>{t.colunaQuando}</TableHead>
                  <TableHead>{t.colunaPagina}</TableHead>
                  <TableHead>{t.colunaNatureza}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campos.map((campo) => (
                  <TableRow
                    key={`${campo.campo}:${campo.valor}`}
                    data-testid="quadro-contrato-preenchido"
                  >
                    <TableCell className="whitespace-nowrap">{campo.campo}</TableCell>
                    <TableCell>
                      <MonoData>{campo.valor}</MonoData>
                    </TableCell>
                    <TableCell>{valorOuTraco(campo.quem)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {campo.quando ? formatarInstante(campo.quando, false) : t.semValor}
                    </TableCell>
                    <TableCell>{valorOuTraco(campo.pagina)}</TableCell>
                    <TableCell>
                      <Badge variant={campo.natureza === 'fato' ? 'danger' : 'warning'}>
                        {t.natureza[campo.natureza]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-sc-1 max-w-prose text-caption text-fg-dim">{t.naturezaNota}</p>
        </>
      )}
    </section>
  );
}

function rotuloDoItem(chave: string): string {
  const itens: Record<string, string> = t.itens;
  return itens[chaveDoRotulo(chave)] ?? chave;
}

function Conferencias({ itens }: { itens: readonly ItemVerificacaoData[] }): JSX.Element {
  return (
    <section data-testid="quadro-contrato-conferencias">
      <Titulo>{t.conferenciasTitulo}</Titulo>
      <ul className="divide-y divide-border border border-border">
        {itens.map((item) => (
          <li
            key={item.chave}
            className="space-y-sc-1 px-sc-3 py-sc-2"
            data-testid={`quadro-contrato-item-${item.chave}`}
          >
            <div className="flex flex-wrap items-center gap-sc-2">
              <SituacaoBadge situacao={item.situacao} />
              <span className="text-body-sm text-fg">{rotuloDoItem(item.chave)}</span>
            </div>
            {item.detalhe ? <p className="text-body-sm text-fg-muted">{item.detalhe}</p> : null}
            {item.obtido ? (
              <p className="break-all text-caption text-fg-dim">
                <MonoData>{item.obtido}</MonoData>
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Diligencias({
  apoio,
  doKit,
}: {
  apoio: ApoioApuradoData | null;
  /** E101.F5: as do kit do dossiê em imagem, depois das do apoio. */
  doKit: readonly string[];
}): JSX.Element | null {
  const diligencias = [...(apoio?.diligencias ?? []), ...doKit];
  if (diligencias.length === 0) return null;
  return (
    <section data-testid="quadro-contrato-diligencias">
      <Titulo>{t.diligenciasTitulo}</Titulo>
      <ul className="list-disc space-y-sc-1 pl-sc-4 text-body-sm text-fg-muted">
        {diligencias.map((diligencia) => (
          <li key={diligencia}>{diligencia}</li>
        ))}
      </ul>
    </section>
  );
}

export function PainelContrato({ resultado }: PainelDoEixoProps): JSX.Element {
  const contrato = contratoDoResultado(resultado);
  if (contrato === null) return <></>;
  return (
    <div className="space-y-sc-4" data-testid="quadro-contrato">
      <ComoLer texto={te.confronta.contrato} testId="quadro-contrato-como-ler" />
      <BemFinanciado bens={contrato.kit?.bens ?? []} />
      {contrato.kit !== null ? (
        <PreenchidosDepois campos={contrato.kit.preenchidos_depois} />
      ) : null}
      <Conferencias itens={itensDoContrato(contrato)} />
      <Diligencias apoio={contrato.apoio} doKit={contrato.kit?.diligencias ?? []} />
    </div>
  );
}
